import React, { useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
} from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useHistory } from 'react-router-dom'
import clsx from 'clsx'

import { useVoiceAgentQuestions } from '../../../hooks/useVoiceAgentQuestions'
import {
    VoiceCallQuestion,
    VoiceCallQuestionPatch,
    VoiceCallQuestionStatus,
} from '../../../models/VoiceAgent'
import { Routes } from '../../../navigation/config/routes'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'

const FILTERS: { id: VoiceCallQuestionStatus; label: string }[] = [
    { id: 'pending', label: 'Needs answer' },
    { id: 'answered', label: 'Answered' },
    { id: 'dismissed', label: 'Dismissed' },
]

const EMPTY_COPY: Record<
    VoiceCallQuestionStatus,
    { title: string; body: string }
> = {
    pending: {
        title: 'No questions waiting',
        body:
            'When a caller asks something the agent cannot answer, it shows up here. Add an answer and the agent will use it on future calls.',
    },
    answered: {
        title: 'No saved answers yet',
        body:
            'Answers you save are read to callers the next time that question comes up.',
    },
    dismissed: {
        title: 'Nothing dismissed',
        body:
            'Questions you dismiss stay here in case you want to restore them.',
    },
}

const formatAsked = (iso: string): string => {
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return ''
    const day = (value: Date) =>
        new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate(),
        ).getTime()
    const days = Math.round((day(new Date()) - day(date)) / 86400000)
    if (days === 0) return 'today'
    if (days === 1) return 'yesterday'
    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
    })
}

export const CallQuestionsPanel: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const history = useHistory()
    const [filter, setFilter] = useState<VoiceCallQuestionStatus>('pending')
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [sort, setSort] = useState<'last_asked' | 'times_asked' | 'created'>(
        'last_asked',
    )
    const [drafts, setDrafts] = useState<Record<number, string>>({})
    const {
        questions,
        count,
        hasNext,
        hasPrevious,
        counts = { pending: 0, answered: 0, dismissed: 0 },
        isLoading,
        error,
        refetch,
        updateQuestion,
    } = useVoiceAgentQuestions(filter, page, {
        search: search.trim(),
        dateFrom,
        dateTo,
        sort,
    })

    const openCall = (callId: number) => {
        history.replace({
            pathname: Routes.manage.voiceAgent.root,
            search: `?tab=calls&call=${callId}`,
        })
    }

    const clearDraft = (id: number) => {
        setDrafts((prev) => {
            const next = { ...prev }
            delete next[id]
            return next
        })
    }

    const update = (id: number, patch: VoiceCallQuestionPatch) => {
        updateQuestion.mutate(
            { id, ...patch },
            { onSuccess: () => clearDraft(id) },
        )
    }

    const isSaving = (id: number) =>
        updateQuestion.isLoading && updateQuestion.variables?.id === id

    const renderQuestion = (question: VoiceCallQuestion) => {
        const answer = drafts[question.id] ?? question.answer
        const trimmed = answer.trim()
        const unchanged = trimmed === question.answer.trim()
        const saving = isSaving(question.id)
        const askedLabel = formatAsked(question.last_asked_at)
        const repeated = question.times_asked > 1

        const save = () => {
            if (saving || !trimmed || unchanged) return
            update(question.id, { answer: trimmed })
        }

        return (
            <Box
                key={question.id}
                className={clsx(
                    classes.questionCard,
                    filter === 'pending' && classes.questionCardPending,
                    filter === 'answered' && classes.questionCardAnswered,
                )}
            >
                <Box className={classes.questionCardBody}>
                    <Typography className={classes.questionQuote}>
                        {question.question}
                    </Typography>
                    <Box className={classes.questionMeta}>
                        <span
                            className={clsx(
                                classes.questionChip,
                                repeated &&
                                    filter === 'pending' &&
                                    classes.questionChipHot,
                            )}
                        >
                            Asked {question.times_asked}{' '}
                            {question.times_asked === 1 ? 'time' : 'times'}
                        </span>
                        {askedLabel && (
                            <span className={classes.questionChip}>
                                Last asked {askedLabel}
                            </span>
                        )}
                        {filter === 'answered' && (
                            <span
                                className={clsx(
                                    classes.questionChip,
                                    classes.questionChipLive,
                                )}
                            >
                                Used on future calls
                            </span>
                        )}
                        {question.source_call_id != null && (
                            <button
                                type="button"
                                className={classes.questionCallLink}
                                onClick={() =>
                                    openCall(question.source_call_id as number)
                                }
                            >
                                View call
                            </button>
                        )}
                    </Box>

                    {filter === 'dismissed' ? (
                        <>
                            {question.answer && (
                                <Typography
                                    className={classes.questionDismissedAnswer}
                                >
                                    {question.answer}
                                </Typography>
                            )}
                            <Box className={classes.questionComposerFoot}>
                                <Typography className={classes.questionHint}>
                                    Restoring puts this back in the queue.
                                </Typography>
                                <Box className={classes.questionActions}>
                                    <Button
                                        className={classes.btnGhost}
                                        onClick={() =>
                                            update(question.id, {
                                                status: 'pending',
                                            })
                                        }
                                        disabled={saving}
                                    >
                                        Restore
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <Box className={classes.questionComposer}>
                            <label
                                className={classes.questionComposerLabel}
                                htmlFor={`call-question-${question.id}`}
                            >
                                What the agent should say
                            </label>
                            <TextField
                                id={`call-question-${question.id}`}
                                value={answer}
                                onChange={(event) =>
                                    setDrafts((prev) => ({
                                        ...prev,
                                        [question.id]: event.target.value,
                                    }))
                                }
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' &&
                                        (event.metaKey || event.ctrlKey)
                                    ) {
                                        event.preventDefault()
                                        save()
                                    }
                                }}
                                placeholder="One or two sentences a caller can hear."
                                fullWidth
                                variant="outlined"
                                size="small"
                                multiline
                                rows={3}
                                className={classes.questionField}
                                inputProps={{
                                    'aria-label': `Answer for ${question.question}`,
                                    maxLength: 4000,
                                }}
                            />
                            <Box className={classes.questionComposerFoot}>
                                <Typography className={classes.questionHint}>
                                    {unchanged && question.answer
                                        ? 'Saved. The agent uses this answer on future calls.'
                                        : trimmed
                                        ? 'Ctrl + Enter to save.'
                                        : 'Leave this blank and the agent will keep using its fallback.'}
                                    {trimmed.length > 280
                                        ? ` ${trimmed.length.toLocaleString()} characters.`
                                        : ''}
                                </Typography>
                                <Box className={classes.questionActions}>
                                    <Button
                                        className={classes.btnGhost}
                                        onClick={() =>
                                            update(question.id, {
                                                status: 'dismissed',
                                            })
                                        }
                                        disabled={saving}
                                    >
                                        Dismiss
                                    </Button>
                                    <Button
                                        className={classes.btnPrimary}
                                        variant="contained"
                                        color="primary"
                                        onClick={save}
                                        disabled={
                                            saving || !trimmed || unchanged
                                        }
                                    >
                                        {saving ? 'Saving...' : 'Save answer'}
                                    </Button>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }

    const empty = EMPTY_COPY[filter]

    return (
        <Box className={classes.questionPage}>
            <Box className={classes.questionHeader}>
                <Box>
                    <Typography className={classes.questionTitle}>
                        Questions callers asked
                    </Typography>
                    <Typography className={classes.questionSubtitle}>
                        These are the questions the agent could not answer. Your
                        reply is given to the agent on the next call.
                    </Typography>
                </Box>
            </Box>

            <Box className={classes.questionToolbar}>
                <TextField
                    className={classes.questionSearch}
                    size="small"
                    variant="outlined"
                    placeholder="Search questions or answers"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                    }}
                    inputProps={{ 'aria-label': 'Search questions' }}
                />
                <TextField
                    className={classes.questionDate}
                    label="From"
                    type="date"
                    size="small"
                    variant="outlined"
                    value={dateFrom}
                    onChange={(event) => {
                        setDateFrom(event.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    className={classes.questionDate}
                    label="To"
                    type="date"
                    size="small"
                    variant="outlined"
                    value={dateTo}
                    onChange={(event) => {
                        setDateTo(event.target.value)
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    className={classes.questionSort}
                    select
                    label="Sort"
                    size="small"
                    variant="outlined"
                    value={sort}
                    onChange={(event) => {
                        setSort(
                            event.target.value as
                                | 'last_asked'
                                | 'times_asked'
                                | 'created',
                        )
                        setPage(1)
                    }}
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ native: true }}
                >
                    <option value="last_asked">Last asked</option>
                    <option value="times_asked">Most asked</option>
                    <option value="created">Newest saved</option>
                </TextField>
                {(search || dateFrom || dateTo) && (
                    <Button
                        className={classes.btnGhost}
                        onClick={() => {
                            setSearch('')
                            setDateFrom('')
                            setDateTo('')
                            setPage(1)
                        }}
                    >
                        Clear
                    </Button>
                )}
            </Box>

            <Box className={classes.questionSegment}>
                {FILTERS.map((item) => {
                    const selected = filter === item.id
                    return (
                        <button
                            key={item.id}
                            type="button"
                            aria-label={item.label}
                            className={clsx(
                                classes.questionSegmentBtn,
                                selected && classes.questionSegmentBtnActive,
                            )}
                            onClick={() => {
                                setFilter(item.id)
                                setPage(1)
                            }}
                        >
                            {item.label}
                            <span
                                aria-hidden="true"
                                className={clsx(
                                    classes.questionCount,
                                    selected &&
                                        item.id === 'pending' &&
                                        classes.questionCountActive,
                                    selected &&
                                        item.id === 'answered' &&
                                        classes.questionCountAnswered,
                                )}
                            >
                                {counts[item.id]}
                            </span>
                        </button>
                    )
                })}
            </Box>

            {isLoading && (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {!isLoading && error && (
                <Box
                    className={clsx(
                        classes.questionCard,
                        classes.questionEmpty,
                    )}
                >
                    <Typography className={classes.questionTitle}>
                        Unable to load questions
                    </Typography>
                    <Typography className={classes.questionEmptyCopy}>
                        Something went wrong. Please try again.
                    </Typography>
                    <Button
                        className={classes.btnPrimary}
                        variant="contained"
                        color="primary"
                        onClick={() => refetch()}
                        style={{ marginTop: 16 }}
                    >
                        Retry
                    </Button>
                </Box>
            )}

            {!isLoading && !error && questions.length === 0 && (
                <Box
                    className={clsx(
                        classes.questionCard,
                        classes.questionEmpty,
                    )}
                >
                    <Box className={classes.emptyIconWrap}>
                        <HelpOutlineIcon />
                    </Box>
                    <Typography className={classes.questionTitle}>
                        {empty.title}
                    </Typography>
                    <Typography className={classes.questionEmptyCopy}>
                        {empty.body}
                    </Typography>
                </Box>
            )}

            {!isLoading && !error && questions.length > 0 && (
                <Box className={classes.questionList}>
                    {questions.map(renderQuestion)}
                </Box>
            )}

            {!isLoading && !error && count > 0 && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Typography variant="caption" color="textSecondary">
                        {count} question{count === 1 ? '' : 's'}
                    </Typography>
                    <Box style={{ display: 'flex', gap: 6 }}>
                        <Button
                            size="small"
                            variant="outlined"
                            className={classes.btnSmall}
                            disabled={!hasPrevious}
                            onClick={() => setPage((current) => current - 1)}
                        >
                            Prev
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            className={classes.btnSmall}
                            disabled={!hasNext}
                            onClick={() => setPage((current) => current + 1)}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
